import { useEffect, useRef, useState, type JSX } from 'react';
import {
  ACTIVEBAR_OFFSET,
  GAP,
  ITEM_WIDTH,
  SCROLL_TIMEOUT_MS,
} from './ScrollableTabs.constants';
import styles from './ScrollableTabs.module.css';
import type { Item, ScrollableTabsProps } from './ScrollableTabs.types';

interface ExtendedScrollableTabsProps extends ScrollableTabsProps {
  shiftTabs?: boolean;
}

const ScrollableTabs = ({
  items,
  onClick,
  onCenteredItemChange,
  initialActiveIndex = 0,
  shiftTabs = false,
}: ExtendedScrollableTabsProps): JSX.Element => {
  const [activeIndex, setActiveIndex] = useState<number>(initialActiveIndex);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const scrollTimeout = useRef<NodeJS.Timeout | null>(null);
  const isScrolling = useRef<boolean>(false);

  // Center the initial active item on mount or when initialActiveIndex changes
  useEffect(() => {
    setActiveIndex(initialActiveIndex);
    if (scrollRef.current && items.length > 0) {
      const container = scrollRef.current;
      const visibleWidth = container.offsetWidth;
      const scrollTo =
        initialActiveIndex * (ITEM_WIDTH + GAP) +
        ITEM_WIDTH / 2 -
        visibleWidth / 2;
      container.scrollTo({ left: scrollTo, behavior: 'auto' });
    }
  }, [initialActiveIndex, items.length]);

  // Handle scroll events with sticky behavior
  useEffect((): (() => void) => {
    const container = scrollRef.current;
    if (!container) return () => {};

    const handleScrollStart = (): void => {
      isScrolling.current = true;
    };

    const handleScroll = (): void => {
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
      scrollTimeout.current = setTimeout(() => {
        isScrolling.current = false;
      }, SCROLL_TIMEOUT_MS);
    };

    let hasScrolled = false;
    const handleFirstScroll = (): void => {
      if (!hasScrolled) {
        hasScrolled = true;
        handleScrollStart();
      }
      handleScroll();
    };

    container.addEventListener('scroll', handleFirstScroll, {
      passive: true,
    });
    return () => {
      container.removeEventListener('scroll', handleFirstScroll);
      if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    };
  }, [items]);

  const handleItemClick = (idx: number, item: Item): void => {
    onClick?.(item);
    setActiveIndex(idx);
    onCenteredItemChange?.(item);
    if (idx > 1 && idx < items.length - 2) {
      const container = scrollRef.current;
      if (container) {
        const visibleWidth = container.offsetWidth;
        const scrollTo =
          idx * (ITEM_WIDTH + GAP) + ITEM_WIDTH / 2 - visibleWidth / 2;
        container.scrollTo({ left: scrollTo, behavior: 'smooth' });
      }
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.rowWrapper}>
        <div
          className={
            shiftTabs
              ? `${styles.scrollWrapper} ${styles.scrollWrapperShifted}`
              : styles.scrollWrapper
          }
          ref={scrollRef}
        >
          <div
            className={styles.activebar}
            style={{
              left: shiftTabs
                ? `calc(${activeIndex * ACTIVEBAR_OFFSET}px + 9%)`
                : activeIndex * ACTIVEBAR_OFFSET,
            }}
          />
          {items.map((item, idx) => (
            <div key={item.id} className={styles.itemWrapper}>
              <div
                className={styles.card}
                onClick={() => handleItemClick(idx, item)}
              >
                <img src={item.iconUrl} alt="" className={styles.cardImg} />
              </div>
              <p className={styles.cardtitle} title={item.name}>
                {item.name}
              </p>
            </div>
          ))}
        </div>
        <div className={styles.bottomlineWrapper}>
          <div className={styles.bottomline} />
        </div>
      </div>
    </div>
  );
};

export default ScrollableTabs;
